import {
  Avatar,
  Blockquote,
  Box,
  Button,
  CheckboxCards,
  Flex,
  Text,
} from "@radix-ui/themes";
import { usePostHog } from "posthog-js/react";
import { useEffect, useLayoutEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useRecoilState } from "recoil";
import {
  bookingDurationLabels,
  formatCurrencyFromCents,
  formatDate,
  formatTime,
} from "use-smart-locks-shared";
import { buildLoginRedirectPath } from "../../authentication/core/client/login-redirect";
import { routes } from "../../routes";
import {
  getApiUserErrorMessage,
  getCampingCheckoutUrl,
  isUnauthorizedApiError,
} from "../../shared/api/nest-api";
import { useDeviceIdQuery } from "../../shared/queries/use-device-id-query";
import { useCampingQuote } from "../hooks/use-camping-quote";
import { useCampingStationSlug } from "../hooks/use-camping-station-slug";
import { selectedUnitIdsState } from "../state/selected-unit-ids.state";
import { containsOnlyParkingLots } from "../unit-type-copy";

export const CampingRentBikeSelectionPage: React.FC = () => {
  const slug = useCampingStationSlug();
  const quote = useCampingQuote();
  const posthog = usePostHog();
  const { data: deviceId } = useDeviceIdQuery();
  const containsParkingLotsOnly = containsOnlyParkingLots(
    quote.units.map((unit) => unit.unitType),
  );

  const [selectedUnitIds, setSelectedUnitIds] = useRecoilState(
    selectedUnitIdsState(slug),
  );

  useEffect(() => {
    posthog.capture("selected_unit_ids", {
      value: selectedUnitIds,
      stationSlug: slug,
    });
  }, [posthog, selectedUnitIds, slug]);

  useLayoutEffect(() => {
    setSelectedUnitIds((current) =>
      current.filter((currentUnitId) =>
        quote.units.some((unit) => unit.id === currentUnitId && unit.available),
      ),
    );
  }, [quote, setSelectedUnitIds]);

  const navigate = useNavigate();
  const [navigating, setNavigating] = useState(false);

  const handleEditBookingPeriod = () => {
    void navigate(routes.camping.rent.index(slug));
  };

  const handleNextClick = () => {
    setNavigating(true);

    getCampingCheckoutUrl(quote.campingStationId, {
      duration: quote.duration,
      pickupDate: new Date(quote.pickupDate),
      unitIds: selectedUnitIds,
      deviceId,
    })
      .then((checkoutResponse) => {
        // Using the in-app browser would be a slightly better user experience, but the redirect is not caught on iOS.
        // I.e. with in-app browser the user doesn't end up in the app after the payment.
        // Maybe we can use custom URL schema in the future: https://github.com/ionic-team/capacitor-plugins/issues/675#issuecomment-957760081
        window.location.href = checkoutResponse.checkoutUrl;
      })
      .catch((error: unknown) => {
        if (isUnauthorizedApiError(error)) {
          void navigate(
            buildLoginRedirectPath(
              window.location.pathname + window.location.search,
              slug,
            ),
            { replace: true },
          );
          return;
        }

        toast.error(
          getApiUserErrorMessage(error) ??
            "Leider ist ein unerwarteter Fehler aufgetreten",
        );
        setNavigating(false);
      });
  };

  const nextEnabled = selectedUnitIds.length > 0;

  return (
    <Flex direction="column" gap="4">
      <Text>
        {containsParkingLotsOnly
          ? "Wählen Sie nun die gewünschten Parkplätze für Ihren Buchungszeitraum aus."
          : "Wählen Sie nun die gewünschten Räder für Ihren Buchungszeitraum aus."}
      </Text>

      <Flex align="center">
        <Blockquote>
          {formatDate(quote.pickupDate)}, ab {formatTime(quote.pickupDate)} für{" "}
          {bookingDurationLabels[quote.duration]}
        </Blockquote>

        <Button
          size="1"
          variant="ghost"
          onClick={handleEditBookingPeriod}
          ml="3"
        >
          (ändern)
        </Button>
      </Flex>

      <Text>
        Die angezeigten Preise sind jeweils für den gesamten Buchungszeitraum.
      </Text>

      <CheckboxCards.Root
        columns="1"
        value={selectedUnitIds}
        onValueChange={setSelectedUnitIds}
      >
        {quote.units.map((unit) => (
          <CheckboxCards.Item
            key={unit.id}
            value={unit.id}
            disabled={!unit.available}
          >
            <Flex gap="3" align="center" width="100%">
              <Avatar
                className="unit"
                size="3"
                src={unit.imageUrl}
                radius="large"
                fallback={unit.name.charAt(0)}
                style={{ objectFit: "cover" }}
              />
              <Box flexGrow="1">
                <Text as="div" size="2" weight="bold">
                  {unit.name}
                </Text>
                <Text as="div" size="2" color="gray">
                  {unit.description}
                </Text>
              </Box>
              <Box width="80px">
                <Text as="div" align="right">
                  {unit.available
                    ? formatCurrencyFromCents(unit.priceInCents)
                    : "ausgebucht"}
                </Text>
              </Box>
            </Flex>
          </CheckboxCards.Item>
        ))}
      </CheckboxCards.Root>

      <Flex justify="end">
        <Button
          onClick={handleNextClick}
          disabled={!nextEnabled || navigating}
          loading={navigating}
        >
          weiter zur Bestellübersicht
        </Button>
      </Flex>
    </Flex>
  );
};
